<script setup lang="ts">
/**
 * KioskShell — minimale Hülle für die Kunden-Rolle (`role === 'client'`).
 *
 * Statt des vollen miraCMS-Chrome (Projektliste, Editor, Settings) sieht der
 * Kunde nur: sein Branding (Logo + Titel), ein Konto-Menü (Passwort/2FA),
 * Logout — und darunter sein Werkzeug (Produkt-Manager). Der eigentliche
 * Käfig (welche Routen erreichbar sind) sitzt im Router-Guard; serverseitig
 * ist der Zugriff ohnehin per AccessControl auf das eigene Projekt begrenzt.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter, RouterLink, useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useProjectsStore } from '@/stores/projects'
import { applyBranding, clearBranding } from '@/composables/useBranding'
import ThemeToggle from '@/components/ThemeToggle.vue'
import PoweredBy from '@/components/PoweredBy.vue'
import NeuerungenKarte from '@/components/NeuerungenKarte.vue'
import UpdateBanner from '@/components/UpdateBanner.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const router = useRouter()
const route = useRoute()
const { user, logout } = useAuth()
const projectsStore = useProjectsStore()

const accountOpen = ref(false)

const projectId = computed<string | null>(() => user.value?.projects?.[0] ?? null)
const project = computed(() =>
  projectId.value ? projectsStore.projects.find((p) => p.id === projectId.value) ?? null : null,
)
const branding = computed(() => project.value?.branding ?? null)
const appName = computed(() => branding.value?.app_name || project.value?.name || 'miraCMS')

// 2FA-Pflicht (höherer Schutzbedarf): Kunde soll 2FA aktivieren, hat es aber
// noch nicht → freundlicher, nicht wegklickbarer Hinweis mit Direkt-Link.
const needs2fa = computed(
  () => branding.value?.require_2fa === true && user.value?.totp_enabled !== true,
)
const onAccountPage = computed(() => route.name === 'account')

async function ensureProject(): Promise<void> {
  if (!projectsStore.projects.length) await projectsStore.loadAll()
}

watch(branding, (b) => applyBranding(b), { immediate: true })

onMounted(async () => {
  await ensureProject()
  applyBranding(branding.value)
})
onUnmounted(() => clearBranding())

async function onLogout(): Promise<void> {
  accountOpen.value = false
  await logout()
  await router.push('/login')
}
</script>

<template>
  <div class="h-svh min-h-svh flex flex-col overflow-hidden bg-bg text-fg">
    <header
      class="sticky top-0 z-30 flex items-center gap-3 border-b border-line
             bg-surface/85 backdrop-blur px-4 py-3 md:px-6 shrink-0"
    >
      <!-- Branding: Logo wenn vorhanden, sonst Titel -->
      <RouterLink
        v-if="projectId"
        :to="`/projects/${projectId}/collections`"
        class="flex items-center gap-2.5 min-w-0 group"
      >
        <img
          v-if="branding?.logo"
          :src="branding.logo"
          :alt="appName"
          class="h-11 sm:h-12 w-auto max-w-48 object-contain"
        />
        <span
          class="font-semibold text-fg group-hover:text-primary transition-colors truncate"
          :class="branding?.logo ? 'text-sm hidden sm:inline' : 'text-base'"
        >{{ appName }}</span>
      </RouterLink>
      <span v-else class="font-semibold text-fg">{{ appName }}</span>

      <!-- Right: Theme + Konto -->
      <div class="ml-auto flex items-center gap-1.5">
        <ThemeToggle />
        <div class="relative pl-2 ml-1 border-l border-line">
          <!-- Klick-daneben schließt das Menü -->
          <button
            v-if="accountOpen"
            type="button"
            class="fixed inset-0 z-30 cursor-default"
            :aria-label="t('ui.menue_schliessen')"
            @click="accountOpen = false"
          ></button>
          <button
            type="button"
            class="relative z-40 flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-hover transition-colors"
            :aria-expanded="accountOpen"
            aria-haspopup="true"
            @click="accountOpen = !accountOpen"
          >
            <span
              class="inline-grid place-items-center w-7 h-7 rounded-full bg-primary text-white text-xs font-semibold"
              aria-hidden="true"
            >{{ (user?.name || user?.username || '?').slice(0, 1).toUpperCase() }}</span>
            <span class="hidden sm:inline text-sm text-fg-mid max-w-30 truncate">{{ user?.name }}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" class="text-fg-muted" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <!-- Dropdown -->
          <div
            v-if="accountOpen"
            class="absolute right-0 mt-1 w-44 rounded-lg border border-line bg-surface shadow-lg py-1 z-40"
            @click="accountOpen = false"
          >
            <RouterLink
              to="/account"
              class="flex items-center gap-2 px-3 py-2 text-sm text-fg-mid hover:bg-hover hover:text-fg"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.8" aria-hidden="true">
                <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
              </svg>{{ t('ui.konto_amp_sicherheit') }}</RouterLink>
            <button
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-fg-mid hover:bg-hover hover:text-fg text-left"
              @click="onLogout"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
              </svg>{{ t('ui.abmelden') }}</button>
          </div>
        </div>
      </div>
    </header>

    <!-- 2FA-Pflicht-Hinweis (nur wenn require_2fa + noch nicht aktiv) -->
    <div
      v-if="needs2fa && !onAccountPage"
      class="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-300 dark:border-amber-800
             px-4 py-2.5 text-sm text-amber-900 dark:text-amber-200 flex items-center gap-2"
      role="alert"
    >
      <span aria-hidden="true">🔒</span>
      <span class="flex-1">{{ t('ui.fuer_dieses_konto_ist_zwei_faktor_schutz_erf') }}</span>
      <RouterLink
        to="/account"
        class="font-medium underline underline-offset-2 hover:no-underline shrink-0"
      >{{ t('ui.jetzt_einrichten') }}</RouterLink>
    </div>

    <!-- Neben <main>, nicht darin: <main> füllt den Rest der Höhe, und der
         Editor darin rechnet mit genau dieser Höhe. -->
    <NeuerungenKarte class="mx-4 my-3 shrink-0 md:mx-6" />

    <main class="flex-1 min-h-0 min-w-0 overflow-y-auto">
      <slot />
    </main>

    <footer class="shrink-0 border-t border-line py-3 px-4">
      <PoweredBy />
    </footer>

    <!-- Bis zum 02.10.2026 hing der Balken nur in der AppShell: Kundinnen
         arbeiteten nach jedem Deploy mit der alten Fassung weiter, bis sie
         selbst neu luden. -->
    <UpdateBanner />
  </div>
</template>
